import styles from "./PuzzleBoard.module.css";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
  FaCog,
  FaStar,
  FaExclamationCircle,
  FaCheck,
  FaHeart,
  FaRegHeart,
  FaSignal,
  FaStepBackward,
  FaStepForward,
} from "react-icons/fa";
import Board from "@/chessBoard/components/Board";
import { Chess } from "@chesshotel/chesslib";
import { convertMoves } from "@/util/moves";
import { ChessSets } from "@chesshotel/constants";
import { cn } from "@/lib/utils";
import { move2uci, uci2move } from "@chesshotel/gameReview";
import { Chess as SanChess, MoveShort, UciMove } from "@chesshotel/chess";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/ToolTip";
import { useSettings } from "@/redux/settings";
import { MoveType } from "@chesshotel/types";
import { useUser } from "@/redux/user";
import { api } from "@/services/api";
import BoardSettingsPopup from "@/chessBoard/components/BoardSettingsPopup";
import ResizableBoardFrame from "@/components/board/ResizableBoardFrame";
import {
  getPuzzleLeagueMedalImageSrc,
  getPuzzleLeagueProgressView,
  PUZZLE_RANK_TIERS,
  getPuzzleLeagueTier,
  getPuzzlePlacementProgressView,
  getPuzzlePointsMilestone,
  PuzzleSolvedProgress,
} from "./puzzleMedals";
import { PuzzleDifficultyKey } from "./puzzleDifficulty";
import PuzzleDifficultyPopup from "./PuzzleDifficultyPopup";
import useTranslation from "next-translate/useTranslation";
import type { Translate } from "next-translate";

enum PuzzleState {
  START,
  CORRECT_MOVE,
  INCORRECT_MOVE,
  HINT,
  DONE,
}

const STARTING_PUZZLE_RATING = 800;

function translatedTierLabel(t: Translate, label: string) {
  return t(`league.tiers.${label.toLowerCase()}`, {}, { fallback: label });
}

function getPuzzleStreakBonus(cleanSolveStreak: number) {
  if (cleanSolveStreak <= 1) return 0;

  return Math.min(40, cleanSolveStreak * 4);
}

export type PuzzleRatingUpdate = {
  season?: number | null;
  newElo: number;
  eloChange: number;
  won?: boolean;
  gameNr?: number;
  leagueName?: string;
  divisionId?: number;
  solvedCount?: number;
  points?: number;
  pointsEarned?: number;
  streakBonus?: number;
  cleanSolveStreak?: number;
  promotionProgress?: number;
  rank?: number | null;
  nextRank?: number | null;
  nextRankPoints?: number | null;
};

type PuzzleBoardProps = {
  medalReached?: number | null;
  milestoneReached?: number | null;
  fen: string;
  moves: MoveShort[] | string;
  onComplete?: () => void;
  onNext?: () => void;
  onResult?: (won: boolean, cleanSolve: boolean) => void;
  onRatingChange?: (ratingUpdate: PuzzleRatingUpdate) => void;
  puzzleRating?: number;
  userElo?: number;
  gameNr?: number;
  leagueName?: string;
  solvedProgress: PuzzleSolvedProgress;
  puzzlePoints: number;
  lastPointsEarned?: number | null;
  lastStreakBonus?: number;
  rank?: number | null;
  nextRank?: number | null;
  nextRankPoints?: number | null;
  cleanSolveStreak?: number;
  promotionProgress?: number;
  nextLoading?: boolean;
  puzzleDifficulty: PuzzleDifficultyKey;
  ratingKFactor?: number;
  ratingRevealed?: boolean;
  onPuzzleDifficultyChange?: (difficulty: PuzzleDifficultyKey) => void;
};

function PuzzleStatus({
  statusTitle,
  statusText,
  puzzleRating,
  lives,
  heartAnimationKey,
  t,
}: {
  statusTitle: ReactNode;
  statusText: ReactNode;
  puzzleRating?: number;
  lives: number;
  heartAnimationKey: number;
  t: Translate;
}) {
  const difficultyLabel = t("league.difficulty", {
    rating: puzzleRating?.toLocaleString() || "?",
  });

  return (
    <div className="py-1">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">{statusTitle}</div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              tabIndex={0}
              aria-label={difficultyLabel}
              className="inline-flex shrink-0 cursor-help items-center gap-1.5 whitespace-nowrap rounded-full bg-athens-gray-100 px-2.5 py-1.5 text-xs font-semibold leading-none text-slate-700 outline-none transition-colors hover:bg-athens-gray-200 focus-visible:ring-2 focus-visible:ring-altblue-300"
            >
              <FaSignal className="text-slate-500" aria-hidden="true" />
              <span aria-hidden="true">
                {puzzleRating?.toLocaleString() || "?"}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            <p>{difficultyLabel}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="mt-1 flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 text-sm">{statusText}</div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              tabIndex={0}
              className="mt-1 shrink-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-altblue-300"
            >
              <PuzzleLives
                lives={lives}
                animationKey={heartAnimationKey}
                t={t}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6}>
            <p>{t("league.ratingLives", { count: lives })}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

function PuzzleLives({
  lives,
  animationKey,
  t,
}: {
  lives: number;
  animationKey: number;
  t: Translate;
}) {
  return (
    <div
      role="status"
      aria-label={t("league.ratingLives", { count: lives })}
      className="flex items-center gap-1 text-xs"
    >
      {[0, 1, 2].map((index) => {
        const filled = index < lives;
        const justLost = animationKey > 0 && index === lives;

        return (
          <span
            key={index}
            className="relative inline-flex h-3.5 w-3.5 items-center justify-center"
          >
            {filled ? (
              <FaHeart className="text-altblue-500" aria-hidden="true" />
            ) : (
              <FaRegHeart className="text-gray-300" aria-hidden="true" />
            )}
            {justLost && (
              <FaHeart
                key={`${animationKey}-${index}`}
                className={cn(
                  styles.heartLost,
                  "pointer-events-none absolute text-altblue-500",
                )}
                aria-hidden="true"
              />
            )}
          </span>
        );
      })}
    </div>
  );
}

function PuzzleProgressBar({
  rating,
  leagueName,
  ratingRevealed,
  solvedProgress,
  placementAttempts,
  puzzlePoints,
  lastPointsEarned,
  rank,
  eloChange,
  milestoneReached,
  medalReached,
  promotionProgress = 0,
  t,
}: {
  milestoneReached?: number | null;
  medalReached?: number | null;
  rating: number;
  leagueName?: string;
  ratingRevealed: boolean;
  solvedProgress: PuzzleSolvedProgress;
  placementAttempts: number;
  puzzlePoints: number;
  lastPointsEarned?: number | null;
  rank?: number | null;
  nextRank?: number | null;
  nextRankPoints?: number | null;
  eloChange: number | null;
  promotionProgress?: number;
  t: Translate;
}) {
  const placement = getPuzzlePlacementProgressView(placementAttempts);
  const milestone = getPuzzlePointsMilestone(puzzlePoints);
  const hasRank = typeof rank === "number" && Number.isFinite(rank) && rank > 0;
  const promotion = getPuzzleLeagueProgressView({ rating, leagueName });
  const placementTier = getPuzzleLeagueTier({ rating: STARTING_PUZZLE_RATING });
  const percent = ratingRevealed ? milestone.percent : placement.percent;
  const currentLeagueLabel = translatedTierLabel(
    t,
    promotion.currentTier.label,
  );
  const nextLeagueLabel = promotion.nextTier
    ? translatedTierLabel(t, promotion.nextTier.label)
    : "";

  return (
    <div className="mt-3 shrink-0 md:mt-5">
      {(milestoneReached != null || medalReached != null) && (
        <div
          role="status"
          className="mb-4 rounded-md bg-white/65 px-3 py-3 text-altblue-700 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-500"
        >
          <div className="flex items-center gap-2 font-semibold">
            {medalReached != null ? (
              <img
                src={getPuzzleLeagueMedalImageSrc({
                  ...promotion.currentTier,
                  rank: medalReached,
                })}
                alt={t("league.newDivisionMedal")}
                className="h-9 w-9 object-contain"
              />
            ) : (
              <FaStar className="text-amber-500" aria-hidden="true" />
            )}
            {medalReached != null
              ? t("league.topRankReached", { rank: medalReached })
              : t("league.pointsReached", {
                  points: milestoneReached!.toLocaleString(),
                })}
          </div>
          <div className="mt-1 text-xs text-slate-600">
            {medalReached != null
              ? milestoneReached != null
                ? t("league.alsoPointsReached", {
                    points: milestoneReached.toLocaleString(),
                  })
                : t("league.newDivisionMedalEarned")
              : t("league.nextPointsGoal", {
                  points: milestone.target.toLocaleString(),
                })}
          </div>
        </div>
      )}
      {ratingRevealed && (
        <div className="mb-5">
          <div className="mb-2 flex items-center justify-between gap-3 text-sm">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold leading-none tabular-nums text-slate-900">
                  {rating}
                </span>
                {eloChange !== null && (
                  <span className="text-sm font-semibold tabular-nums text-altblue-500">
                    {eloChange >= 0 ? "+" : ""}
                    {eloChange}
                  </span>
                )}
              </div>
              <div className="mt-1.5 text-xs text-slate-500">
                {t("league.puzzleRating")}
              </div>
            </div>
          </div>
          <div
            className="h-2 overflow-hidden rounded-full bg-athens-gray-200"
            role="progressbar"
            aria-label={t("league.ratingProgressLabel")}
            aria-valuenow={Math.round(promotion.percent)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full rounded-full bg-altblue-400 transition-[width] duration-300"
              style={{ width: `${promotion.percent}%` }}
            />
          </div>
          <div className="mt-1.5 flex justify-between gap-3 text-xs text-slate-500">
            <span>{currentLeagueLabel}</span>
            <span>
              {promotion.nextTier
                ? promotion.percent >= 100
                  ? t("league.cleanSolvesToLeague", {
                      count: Math.min(3, promotionProgress),
                      league: nextLeagueLabel,
                    })
                  : t("league.leagueAtRating", {
                      league: nextLeagueLabel,
                      rating: promotion.nextRating?.toLocaleString(),
                    })
                : t("league.highestLeague")}
            </span>
          </div>
        </div>
      )}
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-slate-800">
          {ratingRevealed
            ? t("league.nextMilestone", {
                points: milestone.target.toLocaleString(),
              })
            : t("league.firstMedal")}
        </span>
        {!ratingRevealed && (
          <span className="text-xs text-slate-500">
            {t("league.placementPuzzles", {
              solved: placement.solved,
              target: placement.target,
            })}
          </span>
        )}
      </div>
      <div className={cn("flex items-center", !ratingRevealed && "gap-3")}>
        <div
          className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-athens-gray-200"
          role="progressbar"
          aria-label={t(
            ratingRevealed
              ? "league.seasonProgressLabel"
              : "league.placementProgressLabel",
          )}
          aria-valuenow={Math.round(percent)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${percent}%` }}
          />
        </div>
        {!ratingRevealed && (
          <img
            src={getPuzzleLeagueMedalImageSrc(placementTier)}
            alt={t("league.firstMedal")}
            className="h-10 w-10 shrink-0 object-contain opacity-50 grayscale drop-shadow-sm"
          />
        )}
      </div>
      {ratingRevealed && (
        <div className="mt-2 flex items-center justify-between gap-3 text-xs">
          <span className="text-slate-500">
            {t("league.seasonPoints", {
              points: milestone.points.toLocaleString(),
              target: milestone.target.toLocaleString(),
            })}
          </span>
          <Link
            href="/leagues/8"
            className="text-slate-600 underline underline-offset-2 hover:text-slate-900"
          >
            {hasRank
              ? t("league.divisionRank", { rank })
              : t("league.viewStandings")}
          </Link>
        </div>
      )}
    </div>
  );
}

function PuzzleSolutionLine({
  solutionLine,
  t,
}: {
  solutionLine: string;
  t: Translate;
}) {
  if (!solutionLine) return null;

  return (
    <div className="mt-3 overflow-hidden text-sm text-gray-700 border rounded-md border-athens-gray-200 bg-athens-gray-100">
      <div className="border-b border-athens-gray-200 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
        {t("general.solution")}
      </div>
      <div className="px-3 py-2 font-mono text-xs leading-5">
        {solutionLine}
      </div>
    </div>
  );
}

function PuzzleMoveList({
  startFen,
  moves,
  moveIndex,
  selectedMoveIndex,
}: {
  startFen: string;
  moves: MoveShort[];
  moveIndex: number;
  selectedMoveIndex: number;
}) {
  const startFenParts = startFen.split(" ");
  const startTurn = startFenParts[1] === "b" ? "b" : "w";
  const startMoveNumber = Math.max(
    1,
    Number.parseInt(startFenParts[5] || "1", 10) || 1,
  );

  const moveRecords = useMemo(() => {
    let chess: SanChess | null = null;
    try {
      chess = new SanChess(startFen);
    } catch {
      chess = null;
    }

    return moves.map((move, index) => {
      const color = index % 2 === 0 ? startTurn : startTurn === "w" ? "b" : "w";
      const rowNumber =
        startMoveNumber + Math.floor((index + (startTurn === "b" ? 1 : 0)) / 2);

      if (!chess) {
        return {
          ...move,
          color,
          rowNumber,
          san: move2uci(move),
        };
      }

      try {
        const playedMove = chess.move(move);
        return {
          ...move,
          color: playedMove?.color || color,
          rowNumber,
          san: playedMove?.san || move2uci(move),
        };
      } catch {
        return {
          ...move,
          color,
          rowNumber,
          san: move2uci(move),
        };
      }
    });
  }, [moves, startFen, startMoveNumber, startTurn]);

  if (moves.length === 0 || moveIndex <= 0) return null;

  const visibleMoves = moveRecords.slice(0, moveIndex);
  const groupedRows = visibleMoves.reduce<
    {
      rowNumber: number;
      whiteMove: (typeof moveRecords)[number] | null;
      blackMove: (typeof moveRecords)[number] | null;
      whiteIndex: number | null;
      blackIndex: number | null;
    }[]
  >((rows, move, index) => {
    let row = rows.find((item) => item.rowNumber === move.rowNumber);

    if (!row) {
      row = {
        rowNumber: move.rowNumber,
        whiteMove: null,
        blackMove: null,
        whiteIndex: null,
        blackIndex: null,
      };
      rows.push(row);
    }

    if (move.color === "w") {
      row.whiteMove = move;
      row.whiteIndex = index;
    } else {
      row.blackMove = move;
      row.blackIndex = index;
    }

    return rows;
  }, []);

  const rows = groupedRows.map((row) => {
    const rowClass = row.rowNumber % 2 === 0 ? "bg-white/60" : "bg-transparent";
    const isWhiteSelected = row.whiteIndex === selectedMoveIndex - 1;
    const isBlackSelected = row.blackIndex === selectedMoveIndex - 1;

    return (
      <div
        key={`${row.rowNumber}-${row.whiteMove?.from || ""}${
          row.blackMove?.from || ""
        }`}
        className={cn(
          "box-border flex h-[36px] min-h-[36px] w-full shrink-0 items-center text-sm leading-[36px]",
          rowClass,
        )}
      >
        <div className="w-10 shrink-0 pl-3.5 font-semibold text-gray-500/80">
          {row.rowNumber}
        </div>
        <div
          className={cn(
            "flex h-full min-w-0 flex-1 items-center px-2 text-left font-medium",
            isWhiteSelected ? "text-primary" : "text-gray-600",
          )}
        >
          <span className="block truncate">
            {row.whiteMove ? row.whiteMove.san : ""}
          </span>
        </div>
        <div
          className={cn(
            "flex h-full min-w-0 flex-1 items-center px-2 text-left font-medium",
            isBlackSelected ? "text-primary" : "text-gray-600",
          )}
        >
          <span className="block truncate">
            {row.blackMove ? row.blackMove.san : ""}
          </span>
        </div>
      </div>
    );
  });

  return (
    <div className="mt-3 flex max-h-[144px] min-h-[36px] flex-col overflow-y-auto rounded-md text-gray-500">
      {rows}
    </div>
  );
}

function buildPuzzlePositions(startFen: string, moves: MoveShort[]) {
  const positions: { fen: string; lastMove: MoveShort | null }[] = [
    { fen: startFen, lastMove: null },
  ];

  let chess: SanChess | null = null;
  try {
    chess = new SanChess(startFen);
  } catch {
    chess = null;
  }

  if (!chess) return positions;

  for (const move of moves) {
    try {
      chess.move(move);
      positions.push({ fen: chess.fen(), lastMove: move });
    } catch {
      break;
    }
  }

  return positions;
}

export default function PuzzleBoard(props: PuzzleBoardProps) {
  const { t } = useTranslation("puzzles");
  const settings = useSettings();
  const boardSize = settings.boardSize || 504;
  const [puzzleChessSet, setPuzzleChessSet] = useState<string>(
    ChessSets.STAUNTY,
  );
  const [puzzleSoundReady, setPuzzleSoundReady] = useState(false);
  useEffect(() => {
    try {
      const saved = localStorage.getItem("puzzle_chess_set");
      if (
        [
          ChessSets.CHESSHOTEL,
          ChessSets.CHESSHOTEL_NEW,
          ChessSets.STAUNTY,
        ].includes(saved as ChessSets)
      )
        setPuzzleChessSet(saved!);
    } catch {
      /* Keep the default board when storage is unavailable. */
    }
  }, []);
  function selectPuzzleChessSet(value: string) {
    setPuzzleChessSet(value);
    try {
      localStorage.setItem("puzzle_chess_set", value);
    } catch {
      /* The current board still updates. */
    }
  }
  const boardSettings = useMemo(
    () => ({
      ...settings,
      chessSet: puzzleChessSet as ChessSets,
      sound: Boolean(settings.sound && puzzleSoundReady),
    }),
    [settings, puzzleChessSet, puzzleSoundReady],
  );
  const user = useUser();
  const [localElo, setLocalElo] = useState(
    props.userElo || STARTING_PUZZLE_RATING,
  );
  const [eloChange, setEloChange] = useState<number | null>(null);
  const resultRecordedRef = useRef(false);
  const ratingRevealed = props.ratingRevealed ?? true;

  useEffect(() => {
    setLocalElo(props.userElo || STARTING_PUZZLE_RATING);
  }, [props.userElo]);

  const applyRatingChange = (ratingUpdate: PuzzleRatingUpdate) => {
    setLocalElo(ratingUpdate.newElo);
    setEloChange(ratingUpdate.eloChange);
    props.onRatingChange?.(ratingUpdate);
  };

  const updateElo = async (
    actualScore: number,
    cleanSolve: boolean,
    completed: boolean,
  ) => {
    if (user?.memberLoggedIn && user.username && props.puzzleRating) {
      try {
        const response = await api.post("/newpuzzle/rating", {
          puzzleRating: props.puzzleRating,
          won: completed,
          score: actualScore,
          cleanSolve,
        });
        const ratingUpdate = response.data as PuzzleRatingUpdate;
        ratingUpdate.won = completed;
        applyRatingChange(ratingUpdate);
      } catch (e) {
        console.error("Error updating rating", e);
      }
    } else if (props.puzzleRating) {
      const currentElo = props.userElo || localElo || STARTING_PUZZLE_RATING;
      const expectedScore =
        1 / (1 + Math.pow(10, (props.puzzleRating - currentElo) / 400));
      const change = Math.round(
        (props.ratingKFactor || 32) * (actualScore - expectedScore),
      );
      const nextCleanSolveStreak =
        actualScore === 1 && cleanSolve ? (props.cleanSolveStreak || 0) + 1 : 0;
      const streakBonus =
        actualScore === 1 && cleanSolve && ratingRevealed
          ? getPuzzleStreakBonus(nextCleanSolveStreak)
          : 0;
      const basePoints =
        actualScore === 1 && cleanSolve && ratingRevealed
          ? Math.max(1, change)
          : 0;
      const pointsEarned = basePoints + streakBonus;
      applyRatingChange({
        newElo: currentElo + change,
        eloChange: change,
        won: completed,
        points: props.puzzlePoints + pointsEarned,
        pointsEarned,
        streakBonus,
        cleanSolveStreak: nextCleanSolveStreak,
      });
    }
  };

  const [moveIndex, setMoveIndex] = useState(0);
  const [moves, setMoves] = useState<MoveShort[]>([]);
  const [playingWhite, setPlayingWhite] = useState(false);
  const [possibleMoves, setPossibleMoves] = useState({});
  const [fen, setFen] = useState<string | null>(null);
  const [animate, setAnimate] = useState(false);
  const [lastMove, setLastMove] = useState(null);
  const [lives, setLives] = useState(3);
  const [heartAnimationKey, setHeartAnimationKey] = useState(0);
  const [hintedMoveIndex, setHintedMoveIndex] = useState<number | null>(null);
  const [opponentMoving, setOpponentMoving] = useState(false);
  const [puzzleState, setPuzzleState] = useState(PuzzleState.START);
  const [highLightKey, setHighLightKey] = useState(null);
  const [solutionVisible, setSolutionVisible] = useState(false);
  const [viewMoveIndex, setViewMoveIndex] = useState<number | null>(null);
  const [difficultyOpen, setDifficultyOpen] = useState(false);
  const [boardSettingsOpen, setBoardSettingsOpen] = useState(false);
  const chess = useRef(Chess()).current;

  const puzzlePositions = useMemo(
    () => buildPuzzlePositions(props.fen, moves),
    [props.fen, moves],
  );
  const selectedMoveIndex = viewMoveIndex ?? moveIndex;
  const viewedPosition =
    viewMoveIndex !== null ? puzzlePositions[viewMoveIndex] : null;
  const boardFen = viewedPosition?.fen ?? fen;
  const boardLastMove = viewedPosition ? viewedPosition.lastMove : lastMove;
  const boardPossibleMoves = viewMoveIndex === null ? possibleMoves : {};
  const canStepBack = selectedMoveIndex > 0;
  const reviewEnd = solutionVisible ? moves.length : moveIndex;
  const canStepForward = selectedMoveIndex < reviewEnd;

  const recordResult = (
    actualScore: number,
    cleanSolve = false,
    completed = false,
  ) => {
    if (resultRecordedRef.current) return;
    resultRecordedRef.current = true;
    updateElo(actualScore, cleanSolve, completed);
    props.onResult?.(actualScore === 1 && completed, cleanSolve);
  };

  let title = <></>;
  let text = <></>;
  switch (puzzleState) {
    case PuzzleState.START:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-gray-800">
          {t(playingWhite ? "general.whiteToMove" : "general.blackToMove")}
        </span>
      );
      text = (
        <span className="text-muted-foreground">
          {t("general.findBestMovePeriod")}
        </span>
      );
      break;

    case PuzzleState.CORRECT_MOVE:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-altblue-500">
          <FaCheck className="text-sm" aria-hidden="true" />{" "}
          <span>{t("general.correct")}</span>
        </span>
      );
      text = (
        <span className="text-muted-foreground">{t("general.keepGoing")}</span>
      );
      break;

    case PuzzleState.INCORRECT_MOVE:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-slate-800">
          <FaExclamationCircle
            className="text-sm text-amber-600"
            aria-hidden="true"
          />{" "}
          <span>{t("general.notQuite")}</span>
        </span>
      );
      text = (
        <span className="text-muted-foreground">
          {lives > 0
            ? t("league.ratingLifeSummary", { count: lives })
            : t("league.ratingRecordedTry")}
        </span>
      );
      break;

    case PuzzleState.HINT:
      title = (
        <span className="text-lg font-semibold text-slate-800">
          {t(lives > 0 ? "general.hint" : "general.practiceMode")}
        </span>
      );
      text = (
        <span className="text-muted-foreground">
          {lives > 0
            ? t("league.hintWithLives", { count: lives })
            : t("league.hintRatingRecorded")}
        </span>
      );
      break;

    case PuzzleState.DONE:
      title = (
        <span className="flex items-center gap-2 text-lg font-semibold text-altblue-500">
          <FaCheck className="text-sm" aria-hidden="true" />{" "}
          <span>{t("general.solved")}</span>
        </span>
      );
      text = (
        <span className="text-muted-foreground">{t("league.readyNext")}</span>
      );
      break;

    default:
      break;
  }

  function startPuzzle() {
    if (!props.fen) return;

    const valid = chess.load(props.fen, 1);
    if (!valid) return;

    setPuzzleState(PuzzleState.START);
    setLives(3);
    setHeartAnimationKey(0);
    setHintedMoveIndex(null);
    setOpponentMoving(false);
    setMoveIndex(1);
    setEloChange(null);
    setSolutionVisible(false);
    setViewMoveIndex(null);
    setHighLightKey(null);
    setPuzzleSoundReady(false);
    resultRecordedRef.current = false;

    const puzzleMoves =
      typeof props.moves === "string"
        ? (props.moves.split(" ").filter(Boolean) as UciMove[]).map((move) =>
            uci2move(move),
          )
        : props.moves;

    const firstMove = puzzleMoves[0];
    if (!firstMove) return;

    chess.move(firstMove);
    setLastMove(firstMove);
    const fen = chess.fen();
    const playingWhite = chess.turn() === "w";

    setPlayingWhite(playingWhite);
    setMoves(puzzleMoves);
    setPossibleMoves(convertMoves(chess.moves({ verbose: true })));
    setAnimate(true);
    setFen(fen);
  }

  useEffect(() => {
    startPuzzle();
  }, [props.fen]);

  useEffect(() => {
    if (fen) setPuzzleSoundReady(true);
  }, [fen]);

  function moveMade(move) {
    if (
      viewMoveIndex !== null ||
      !moves.length ||
      puzzleState === PuzzleState.DONE
    ) {
      return;
    }

    setHighLightKey(null);
    const correctMove = moves[moveIndex];
    if (!correctMove) return;

    if (
      move.from === correctMove.from &&
      move.to === correctMove.to &&
      move.promotion === correctMove.promotion
    ) {
      if (moves.length === moveIndex + 1) {
        setFen(move.fen);
        setPossibleMoves({});
        setPuzzleState(PuzzleState.DONE);
        setMoveIndex(moveIndex + 1);
        const actualScore =
          lives === 3 ? 1 : lives === 2 ? 0.5 : lives === 1 ? 0.25 : 0;
        recordResult(actualScore, lives === 3, true);
        props.onComplete?.();
      } else {
        const nextMove = moves[moveIndex + 1];
        chess.move(move);
        setMoveIndex(moveIndex + 1);
        setOpponentMoving(true);

        chess.move(nextMove);
        const fen = chess.fen();
        const nextPossibleMoves = convertMoves(chess.moves({ verbose: true }));
        setTimeout(() => {
          setFen(fen);
          setPossibleMoves(nextPossibleMoves);
          setMoveIndex(moveIndex + 2);
          setLastMove(nextMove);
          setOpponentMoving(false);
        }, 300);
        setPuzzleState(PuzzleState.CORRECT_MOVE);
      }
    } else {
      if (lives > 0) {
        const nextLives = lives - 1;
        if (lives === 3) props.onResult?.(false, false);
        setLives(nextLives);
        setHeartAnimationKey((key) => key + 1);
        if (nextLives === 0) recordResult(0, false, false);
      }
      setFen(move.fen);
      setPossibleMoves(possibleMoves);
      setMoveIndex(moveIndex);
      setPuzzleState(PuzzleState.INCORRECT_MOVE);
      setTimeout(() => {
        setFen(fen);
      }, 300);
    }
  }

  const solutionLine = useMemo(
    () => moves.map((move) => move2uci(move)).join(" "),
    [moves],
  );

  const showHint = () => {
    if (
      lives === 0 ||
      opponentMoving ||
      hintedMoveIndex === moveIndex ||
      puzzleFinished
    ) {
      return;
    }
    const nextLives = lives - 1;
    if (lives === 3) props.onResult?.(false, false);
    setLives(nextLives);
    setHeartAnimationKey((key) => key + 1);
    setHintedMoveIndex(moveIndex);
    setPuzzleState(PuzzleState.HINT);
    if (nextLives === 0) recordResult(0, false, false);
    setViewMoveIndex(null);
    const move = moves[moveIndex];
    if (move) setHighLightKey(move.from);
  };

  const stepPuzzleMove = (delta: number) => {
    const nextMoveIndex = Math.max(
      0,
      Math.min(reviewEnd, selectedMoveIndex + delta),
    );
    setViewMoveIndex(nextMoveIndex === moveIndex ? null : nextMoveIndex);
    setHighLightKey(null);
  };

  const puzzleFinished = puzzleState === PuzzleState.DONE;
  const canShowSolution = puzzleFinished || lives === 0;
  const panelHeight = settings.isDesktop ? boardSize + 44 + "px" : undefined;
  const hasPlayedSolutionMove = moveIndex > 1;
  const progressBar = (
    <PuzzleProgressBar
      medalReached={puzzleFinished ? props.medalReached : null}
      milestoneReached={puzzleFinished ? props.milestoneReached : null}
      rating={localElo}
      leagueName={props.leagueName}
      ratingRevealed={ratingRevealed}
      solvedProgress={props.solvedProgress}
      placementAttempts={props.gameNr || 0}
      puzzlePoints={props.puzzlePoints}
      lastPointsEarned={puzzleFinished ? props.lastPointsEarned : null}
      eloChange={eloChange}
      rank={props.rank}
      promotionProgress={props.promotionProgress}
      nextRank={props.nextRank}
      nextRankPoints={props.nextRankPoints}
      t={t}
    />
  );

  const tier = getPuzzleLeagueTier({
    rating: localElo,
    leagueName: props.leagueName,
  });
  const tierLabel = translatedTierLabel(t, tier.label);
  const medalRank =
    props.rank && props.rank > 0
      ? [...PUZZLE_RANK_TIERS]
          .reverse()
          .find((threshold) => props.rank! <= threshold)
      : undefined;
  const leagueHeader = ratingRevealed ? (
    <Link
      href="/leagues/8"
      className="flex items-center gap-2 text-white hover:text-white hover:opacity-90"
      aria-label={t("league.viewStandingsLabel", { league: tierLabel })}
    >
      <img
        src={getPuzzleLeagueMedalImageSrc({
          ...tier,
          rank: medalRank || tier.rank,
        })}
        alt=""
        className="h-7 w-7 object-contain"
      />
      <span>{t("league.leagueName", { league: tierLabel })}</span>
    </Link>
  ) : (
    <span>{t("league.title")}</span>
  );

  return (
    <div className="flex flex-col items-center md:mt-4">
      <div className="flex w-full items-center justify-between bg-chess-ui-header px-4 py-2 text-sm font-semibold text-white md:hidden">
        {leagueHeader}
        {!ratingRevealed && (
          <span className="text-xs font-medium text-white/80">
            {t("league.placement")}
          </span>
        )}
      </div>
      <div className="flex flex-col items-center w-full md:w-auto md:flex-row md:items-start md:rounded-lg">
        <div className="flex justify-center md:mr-4">
          <ResizableBoardFrame
            size={boardSize}
            enabled={settings.isDesktop !== false}
          >
            <Board
              showBoardSettings
              onBoardSettingsClick={() => setBoardSettingsOpen(true)}
              lastMove={boardLastMove}
              settings={boardSettings}
              fen={boardFen}
              animate={animate}
              size={boardSize}
              playingWhite={playingWhite}
              possibleMoves={boardPossibleMoves}
              onMoveMade={(move) => moveMade(move)}
              highLightKey={highLightKey}
              showLastMove
            />
          </ResizableBoardFrame>
        </div>

        <div
          className="flex h-full w-full flex-col items-stretch md:rounded-lg bg-chess-ui-panel text-sm md:mb-0 md:w-[400px] lg:w-[400px]"
          style={{ height: panelHeight }}
        >
          <div
            className={cn(
              styles.panelHeader,
              "hidden md:flex shrink-0 items-center justify-between gap-3 rounded-t-lg bg-chess-ui-header px-5 py-4 text-sm font-semibold text-white",
            )}
          >
            {leagueHeader}
            {!ratingRevealed && (
              <span className="text-xs font-medium text-white/80">
                {t("league.placement")}
              </span>
            )}
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 md:flex-1 md:overflow-y-auto px-3 pt-3 md:p-4">
              <div className="shrink-0 rounded-lg bg-chess-ui-surface p-3 md:p-4">
                <PuzzleStatus
                  statusTitle={title}
                  statusText={text}
                  puzzleRating={props.puzzleRating}
                  lives={lives}
                  heartAnimationKey={heartAnimationKey}
                  t={t}
                />

                {lives === 0 &&
                  puzzleState !== PuzzleState.INCORRECT_MOVE &&
                  puzzleState !== PuzzleState.HINT &&
                  !puzzleFinished && (
                    <div className="mt-3 border-t border-athens-gray-200 pt-3">
                      <div className="font-semibold text-slate-800">
                        {t("general.practiceMode")}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {t("general.ratingRecordedKeepSolving")}
                      </div>
                    </div>
                  )}
              </div>
              <div className="hidden md:block">{progressBar}</div>

              {canShowSolution && solutionVisible && (
                <PuzzleMoveList
                  startFen={props.fen}
                  moves={moves}
                  moveIndex={reviewEnd}
                  selectedMoveIndex={selectedMoveIndex}
                />
              )}
            </div>
            <div className="shrink-0 md:rounded-b-lg md:border-t border-athens-gray-200 md:bg-athens-gray-200 p-3 md:p-4">
              <div>
                {puzzleFinished || lives === 0 ? (
                  <Button
                    variant="default"
                    size="lg"
                    className="w-full py-6 text-base font-semibold shadow-sm"
                    disabled={props.nextLoading}
                    onClick={() => props.onNext?.()}
                  >
                    {t("general.nextPuzzle")}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full bg-chess-ui-control text-slate-700 hover:bg-white"
                    onClick={showHint}
                    disabled={
                      lives === 0 ||
                      opponentMoving ||
                      hintedMoveIndex === moveIndex
                    }
                  >
                    {t("general.hint")}
                  </Button>
                )}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div
                    className={cn(
                      "flex items-center gap-2",
                      !solutionVisible && "invisible",
                    )}
                    aria-hidden={!solutionVisible}
                  >
                    <Button
                      variant="quiet"
                      size="icon"
                      disabled={!solutionVisible || !canStepBack}
                      onClick={() => stepPuzzleMove(-1)}
                      aria-label={t("league.previousMove")}
                    >
                      <FaStepBackward />
                    </Button>
                    <Button
                      variant="quiet"
                      size="icon"
                      disabled={!solutionVisible || !canStepForward}
                      onClick={() => stepPuzzleMove(1)}
                      aria-label={t("league.nextMove")}
                    >
                      <FaStepForward />
                    </Button>
                  </div>
                  <div className="flex items-center gap-1">
                    {canShowSolution && (
                      <Button
                        variant="quiet"
                        aria-expanded={solutionVisible}
                        onClick={() => {
                          setSolutionVisible(!solutionVisible);
                          setViewMoveIndex(null);
                        }}
                      >
                        {t(
                          solutionVisible
                            ? "general.hideSolution"
                            : "general.solution",
                        )}
                      </Button>
                    )}
                    <Button
                      variant="quiet"
                      size="icon"
                      onClick={() => setDifficultyOpen(true)}
                      aria-label={t("league.settings")}
                    >
                      <FaCog />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-3 pb-4 md:hidden">{progressBar}</div>
          </div>
        </div>
      </div>
      {boardSettingsOpen && (
        <BoardSettingsPopup
          selectedSet={puzzleChessSet}
          onSelect={selectPuzzleChessSet}
          onClose={() => setBoardSettingsOpen(false)}
        />
      )}
      {difficultyOpen && (
        <PuzzleDifficultyPopup
          selectedDifficulty={
            ratingRevealed ? props.puzzleDifficulty : "balanced"
          }
          lockedToBalanced={!ratingRevealed}
          onSelect={(difficulty) =>
            props.onPuzzleDifficultyChange?.(difficulty)
          }
          onClose={() => setDifficultyOpen(false)}
          t={t}
        />
      )}
    </div>
  );
}
